// StandardsPanel.jsx — Governing Standards an AISC fabricator/erector must keep
// current copies of on hand (per ANSI/AISC 207 certification). Reference list:
// no dashboards, no KPIs. Editions are reference — the project spec governs.
import React, { useState, useEffect, useCallback } from "react";
import { todayLocalISO } from "../../utils/dateFmt";
import {
  Card, Stack, Chip, Box, Typography, Alert, Button, Snackbar,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Grid,
} from "@mui/material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../SmartTable/SmartTable";
const TAG_COLOR = { Core: "primary", Seismic: "error", Bridge: "info", Coating: "secondary", Erection: "warning", Reference: "default", Conditional: "default" };

// Pull comparable edition tokens (year or "16th") from an edition string.
const tokens = (e) => (String(e || "").toLowerCase().match(/\d{4}|\d+(?:st|nd|rd|th)/g) || []);
const isComparable = (e) => tokens(e).length > 0; // "latest"/"current" → not comparable
// Status of a standard given its on-hand record.
function statusFor(std, rec) {
  if (!rec) return { label: "Unconfirmed", color: "default" };
  if (rec.on_hand === "N") return { label: "Not on hand", color: "error" };
  if (rec.on_hand !== "Y") return { label: "Unconfirmed", color: "default" };
  const held = rec.edition_held;
  if (!held) return { label: "On hand", color: "success" };
  if (!isComparable(std.edition)) return { label: `On hand · ${held}`, color: "success" };
  const cur = tokens(std.edition);
  const has = tokens(held);
  const match = cur.some((t) => has.includes(t));
  return match
    ? { label: `Current ✓ (${held})`, color: "success" }
    : { label: `Superseded — held ${held}`, color: "warning" };
}

const STANDARDS = [
  // Design & Standard Practice
  { cat: "Design & Standard Practice", code: "ANSI/AISC 360-22", title: "Specification for Structural Steel Buildings", body: "AISC", governs: "Design of structural steel buildings (LRFD & ASD).", edition: "2022", tags: ["Core"] },
  { cat: "Design & Standard Practice", code: "ANSI/AISC 303-22", title: "Code of Standard Practice for Steel Buildings & Bridges", body: "AISC", governs: "Trade practices, tolerances, responsibilities, fabrication & erection.", edition: "2022", tags: ["Core"] },
  { cat: "Design & Standard Practice", code: "AISC Steel Construction Manual", title: "Steel Construction Manual, 16th Edition", body: "AISC", governs: "Design aids, section properties, connection design (companion to 360-22).", edition: "16th (2023)", tags: ["Core"] },
  { cat: "Design & Standard Practice", code: "ANSI/AISC 341-22", title: "Seismic Provisions for Structural Steel Buildings", body: "AISC", governs: "Seismic load-resisting systems.", edition: "2022", tags: ["Seismic"] },
  { cat: "Design & Standard Practice", code: "ANSI/AISC 358-22", title: "Prequalified Connections for Special & Intermediate Moment Frames", body: "AISC", governs: "Prequalified seismic moment connections.", edition: "2022", tags: ["Seismic"] },

  // Certification
  { cat: "Certification", code: "ANSI/AISC 207-25", title: "Certification Standard for Steel Fabrication & Erection, and Manufacturing", body: "AISC", governs: "The AISC certification program requirements.", edition: "2025", tags: ["Core"] },

  // Welding
  { cat: "Welding", code: "AWS D1.1/D1.1M:2025", title: "Structural Welding Code — Steel", body: "AWS", governs: "Welding of structural steel — design, qualification, fabrication, inspection. (2020 still widely specified.)", edition: "2025", tags: ["Core"] },
  { cat: "Welding", code: "AWS D1.8/D1.8M:2021", title: "Structural Welding Code — Seismic Supplement", body: "AWS", governs: "Seismic welding requirements (supplements D1.1).", edition: "2021", tags: ["Seismic"] },
  { cat: "Welding", code: "AWS D1.5/D1.5M:2020", title: "Bridge Welding Code", body: "AASHTO/AWS", governs: "Welding of steel highway bridges.", edition: "2020", tags: ["Bridge"] },
  { cat: "Welding", code: "AWS A2.4", title: "Standard Symbols for Welding, Brazing, and NDE", body: "AWS", governs: "Weld & NDE symbols on drawings.", edition: "latest", tags: ["Reference"] },
  { cat: "Welding", code: "AWS A3.0/A3.0M", title: "Standard Welding Terms and Definitions", body: "AWS", governs: "Welding terminology.", edition: "latest", tags: ["Reference"] },
  { cat: "Welding", code: "ASME BPVC Section IX", title: "Welding, Brazing, and Fusing Qualifications", body: "ASME", governs: "Procedure & performance qualification where contract requires.", edition: "latest", tags: ["Conditional"] },

  // Bolting
  { cat: "Bolting", code: "RCSC Specification (2020)", title: "Specification for Structural Joints Using High-Strength Bolts", body: "RCSC", governs: "High-strength bolt installation, pretension & slip-critical joints.", edition: "2020", tags: ["Core"] },
  { cat: "Bolting", code: "ASTM F3125/F3125M", title: "High Strength Structural Bolts (consolidates A325/A490)", body: "ASTM", governs: "Structural bolt material specification.", edition: "latest", tags: ["Core"] },
  { cat: "Bolting", code: "ASTM F3148", title: "High Strength Structural Bolt Assemblies (twist-off, 144 ksi)", body: "ASTM", governs: "Tension-control bolt assemblies (if used).", edition: "latest", tags: ["Conditional"] },

  // Material
  { cat: "Material", code: "ASTM A6/A6M", title: "General Requirements for Rolled Structural Steel", body: "ASTM", governs: "Bars, plates, shapes, sheet piling — general requirements & tolerances.", edition: "latest", tags: ["Core"] },
  { cat: "Material", code: "ASTM A992/A992M", title: "Steel for Structural Shapes (W-shapes, 50 ksi)", body: "ASTM", governs: "Standard W-shape material.", edition: "latest", tags: ["Core"] },
  { cat: "Material", code: "ASTM A572/A572M", title: "HSLA Columbium-Vanadium Structural Steel", body: "ASTM", governs: "Plate & shape HSLA grades.", edition: "latest", tags: ["Core"] },
  { cat: "Material", code: "ASTM A36/A36M", title: "Carbon Structural Steel", body: "ASTM", governs: "Carbon steel plate & bar.", edition: "latest", tags: ["Core"] },
  { cat: "Material", code: "ASTM A500/A500M", title: "Cold-Formed Welded & Seamless HSS", body: "ASTM", governs: "Hollow structural sections.", edition: "latest", tags: ["Core"] },
  { cat: "Material", code: "ASTM A563 / F436", title: "Nuts / Hardened Washers", body: "ASTM", governs: "Bolting hardware (nuts, washers).", edition: "latest", tags: ["Core"] },

  // Coating
  { cat: "Coating", code: "SSPC-PA 2 (AMPP)", title: "Measurement of Dry Film Thickness with Magnetic Gauges", body: "AMPP/SSPC", governs: "DFT acceptance of coatings.", edition: "latest", tags: ["Coating"] },
  { cat: "Coating", code: "SSPC-SP Series (AMPP)", title: "Surface Preparation Standards (SP 1, SP 6, SP 10, …)", body: "AMPP/SSPC", governs: "Blast & surface-prep cleanliness grades.", edition: "latest", tags: ["Coating"] },

  // Erection
  { cat: "Erection", code: "OSHA 29 CFR 1926 Subpart R", title: "Safety Standards for Steel Erection", body: "OSHA", governs: "Federal safety regulation for steel erection.", edition: "current", tags: ["Erection"] },
];

const CATS = ["All", "Design & Standard Practice", "Certification", "Welding", "Bolting", "Material", "Coating", "Erection"];

export default function StandardsPanel() {
  const [cat, setCat] = useState("All");
  const [records, setRecords] = useState({});
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ on_hand: "Y", edition_held: "", verified_by: "", verified_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await ApiCall.get("/inventory/standards");
      const map = {};
      (r?.data?.data?.records || []).forEach((rec) => { map[rec.standard_code] = rec; });
      setRecords(map);
    } catch (e) { /* reference list still renders without records */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = STANDARDS.reduce((a, s) => ((a[s.cat] = (a[s.cat] || 0) + 1), a), {});
  const shown = cat === "All" ? STANDARDS : STANDARDS.filter((s) => s.cat === cat);

  const openConfirm = (s) => {
    const rec = records[s.code];
    setActive(s);
    setForm({
      on_hand: rec?.on_hand || "Y",
      edition_held: rec?.edition_held || (isComparable(s.edition) ? s.edition : ""),
      verified_by: rec?.verified_by || "",
      verified_date: rec?.verified_date ? String(rec.verified_date).slice(0, 10) : todayLocalISO(),
      notes: rec?.notes || "",
    });
    setOpen(true);
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await ApiCall.post("/inventory/standards", { standard_code: active.code, current_edition: active.edition, ...form });
      setMsg(`${active.code} recorded.`);
      setOpen(false);
      load();
    } catch (e) { setMsg("Save failed."); } finally { setSaving(false); }
  };
  const previewSuperseded = active && form.on_hand === "Y" && form.edition_held && isComparable(active.edition)
    && statusFor(active, { on_hand: "Y", edition_held: form.edition_held }).color === "warning";

  return (
    <Card sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        AISC-certified fabricators and erectors must keep <strong>current copies of every applicable
        standard on hand</strong> (per ANSI/AISC 207). Use <strong>Confirm</strong> to record the edition your
        shop holds — it is checked against the current edition and flagged if superseded. The edition named in
        your project specification governs.
      </Alert>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip label={`All (${STANDARDS.length})`} color={cat === "All" ? "primary" : "default"}
          variant={cat === "All" ? "filled" : "outlined"} onClick={() => setCat("All")} />
        {CATS.filter((c) => c !== "All").map((c) => (
          <Chip key={c} label={`${c} (${counts[c] || 0})`} color={cat === c ? "primary" : "default"}
            variant={cat === c ? "filled" : "outlined"} onClick={() => setCat(c)} />
        ))}
      </Stack>

      <TableContainer>
        <SmartTable tableId="standards" lockedColumns={["Standard", "Current Edition", "Status"]} size="small">
          <TableHead>
            <TableRow>
              <TableCell><b>Standard</b></TableCell><TableCell><b>Title</b></TableCell>
              <TableCell><b>Current Edition</b></TableCell><TableCell><b>Status</b></TableCell>
              <TableCell align="right"><b>Check</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((s) => {
              const rec = records[s.code];
              const st = statusFor(s, rec);
              return (
                <TableRow key={s.code} hover>
                  <TableCell>
                    <strong>{s.code}</strong>
                    <Typography variant="caption" display="block" color="text.secondary">{s.body}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {s.tags.map((t) => <Chip key={t} size="small" label={t} color={TAG_COLOR[t] || "default"} variant={t === "Core" ? "filled" : "outlined"} />)}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2">{s.title}</Typography><Typography variant="caption" color="text.secondary">{s.governs}</Typography></TableCell>
                  <TableCell>{s.edition}</TableCell>
                  <TableCell>
                    <Chip size="small" color={st.color} label={st.label} variant={st.color === "default" ? "outlined" : "filled"} />
                    {rec?.verified_by ? <Typography variant="caption" display="block" color="text.secondary">{rec.verified_by}{rec.verified_date ? " · " + String(rec.verified_date).slice(0, 10) : ""}</Typography> : null}
                  </TableCell>
                  <TableCell align="right"><Button size="small" variant="outlined" onClick={() => openConfirm(s)}>{rec ? "Update" : "Confirm"}</Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </SmartTable>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm standard on hand</DialogTitle>
        <DialogContent dividers>
          {active && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2">{active.code} — {active.title}</Typography>
                <Typography variant="caption" color="text.secondary">Current edition: <strong>{active.edition}</strong></Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small"><InputLabel>On hand?</InputLabel>
                    <Select label="On hand?" value={form.on_hand} onChange={(e) => setF("on_hand", e.target.value)}>
                      <MenuItem value="Y">Yes</MenuItem><MenuItem value="N">No</MenuItem>
                    </Select></FormControl>
                </Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Edition held" placeholder="e.g. 2022" value={form.edition_held} onChange={(e) => setF("edition_held", e.target.value)} disabled={form.on_hand === "N"} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" label="Verified by" value={form.verified_by} onChange={(e) => setF("verified_by", e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Verified date" InputLabelProps={{ shrink: true }} value={form.verified_date} onChange={(e) => setF("verified_date", e.target.value)} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></Grid>
              </Grid>
              {previewSuperseded && (
                <Alert severity="warning">Edition held does not match the current edition ({active.edition}). This will be flagged as <strong>superseded</strong>.</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" onClose={() => setMsg(null)}>{msg}</Alert>
      </Snackbar>
    </Card>
  );
}
